import React, { useState, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import AddIcon from "@material-ui/icons/Add";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";

import { i18n } from "../../translate/i18n";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: "#eee",
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		backgroundColor: "#eee",
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	contactAvatar: {
		margin: 15,
		width: 160,
		height: 160,
	},
	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		"& > *": {
			margin: 4,
		},
	},
	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},
	tagsSection: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	tagsWrapper: {
		display: "flex",
		flexWrap: "wrap",
		gap: 4,
		marginTop: 8,
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, loading, ticket }) => {
	const classes = useStyles();

	const [modalOpen, setModalOpen] = useState(false);
	const [allTags, setAllTags] = useState([]);
	const [ticketTags, setTicketTags] = useState([]);
	const [anchorEl, setAnchorEl] = useState(null);

	useEffect(() => {
		if (!open || !ticket) return;
		const fetchTags = async () => {
			try {
				const { data } = await api.get("/tags");
				setAllTags(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchTags();
		setTicketTags(ticket.tags || []);
	}, [open, ticket]);

	useEffect(() => {
		if (ticket?.tags) setTicketTags(ticket.tags);
	}, [ticket?.tags]);

	const handleAddTag = async (tagId) => {
		try {
			const { data } = await api.post(`/tags/${tagId}/ticket/${ticket.id}`);
			setTicketTags(data.tags || []);
		} catch (err) {
			toastError(err);
		}
		setAnchorEl(null);
	};

	const handleRemoveTag = async (tagId) => {
		try {
			const { data } = await api.delete(`/tags/${tagId}/ticket/${ticket.id}`);
			setTicketTags(data.tags || []);
		} catch (err) {
			toastError(err);
		}
	};

	const availableTags = allTags.filter(t => !ticketTags.find(tt => tt.id === t.id));

	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div className={classes.header}>
				<IconButton onClick={handleDrawerClose}>
					<CloseIcon />
				</IconButton>
				<Typography style={{ justifySelf: "center" }}>
					{i18n.t("contactDrawer.header")}
				</Typography>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					<Paper square variant="outlined" className={classes.contactHeader}>
						<Avatar
							alt={contact.name}
							src={contact.profilePicUrl}
							className={classes.contactAvatar}
						></Avatar>

						<Typography>{contact.name}</Typography>
						<Typography>
							<Link href={`tel:${contact.number}`}>{contact.number}</Link>
						</Typography>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => setModalOpen(true)}
						>
							{i18n.t("contactDrawer.buttons.edit")}
						</Button>
					</Paper>

					{/* Tags Section */}
					{ticket && (
						<Paper square variant="outlined" className={classes.tagsSection}>
							<Box display="flex" alignItems="center" justifyContent="space-between">
								<Typography variant="subtitle1">Etiquetas</Typography>
								<IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
									<AddIcon fontSize="small" />
								</IconButton>
								<Menu
									anchorEl={anchorEl}
									open={Boolean(anchorEl)}
									onClose={() => setAnchorEl(null)}
								>
									{availableTags.length === 0 && (
										<MenuItem disabled>Sin etiquetas disponibles</MenuItem>
									)}
									{availableTags.map(tag => (
										<MenuItem key={tag.id} onClick={() => handleAddTag(tag.id)}>
											<Chip
												size="small"
												label={tag.name}
												style={{ backgroundColor: tag.color, color: "#fff", marginRight: 8 }}
											/>
										</MenuItem>
									))}
								</Menu>
							</Box>
							<div className={classes.tagsWrapper}>
								{ticketTags.map(tag => (
									<Chip
										key={tag.id}
										label={tag.name}
										size="small"
										onDelete={() => handleRemoveTag(tag.id)}
										style={{ backgroundColor: tag.color, color: "#fff" }}
									/>
								))}
								{ticketTags.length === 0 && (
									<Typography variant="caption" color="textSecondary">
										Sin etiquetas asignadas
									</Typography>
								)}
							</div>
						</Paper>
					)}

					<Paper square variant="outlined" className={classes.contactDetails}>
						<ContactModal
							open={modalOpen}
							onClose={() => setModalOpen(false)}
							contactId={contact.id}
						></ContactModal>
						<Typography variant="subtitle1">
							{i18n.t("contactDrawer.extraInfo")}
						</Typography>
						{contact?.extraInfo?.map(info => (
							<Paper
								key={info.id}
								square
								variant="outlined"
								className={classes.contactExtraInfo}
							>
								<InputLabel>{info.name}</InputLabel>
								<Typography component="div" noWrap style={{ paddingTop: 2 }}>
									<MarkdownWrapper>{info.value}</MarkdownWrapper>
								</Typography>
							</Paper>
						))}
					</Paper>
				</div>
			)}
		</Drawer>
	);
};

export default ContactDrawer;
